import { useState, useRef, useEffect } from "react";
import { Play, Square, Repeat } from "lucide-react";
import { Piano } from "@/components/Piano";
import { playChord } from "@/lib/audio";
import { noteName } from "@/data/modes";
import { useT, chordLabel, noteLabel } from "@/i18n";

export const PracticeView = ({ routines, intro, heading }) => {
  const { t, lang } = useT();
  const [routineId, setRoutineId] = useState(routines[0].id);
  const [tempo, setTempo] = useState(1500);
  const [running, setRunning] = useState(false);
  const [stepIndex, setStepIndex] = useState(-1);
  const [practiceFlash, setPracticeFlash] = useState([]);
  const timers = useRef([]);

  const routine = routines.find((r) => r.id === routineId) || routines[0];
  const routineChords = routine.steps;

  const clearTimers = () => {
    timers.current.forEach((tm) => clearTimeout(tm));
    timers.current = [];
  };

  const stopPractice = () => {
    clearTimers();
    setRunning(false);
    setStepIndex(-1);
    setPracticeFlash([]);
  };

  const startPractice = () => {
    clearTimers();
    setRunning(true);
    const total = routineChords.length;
    for (let loop = 0; loop < 2; loop++) {
      routineChords.forEach((c, i) => {
        const order = loop * total + i;
        const tm = setTimeout(() => {
          setStepIndex(i);
          setPracticeFlash(c.notes);
          playChord(c.notes);
        }, order * tempo);
        timers.current.push(tm);
      });
    }
    const end = setTimeout(() => stopPractice(), 2 * total * tempo);
    timers.current.push(end);
  };

  useEffect(() => () => clearTimers(), []);
  useEffect(() => {
    stopPractice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routineId, tempo]);

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8">
      <div className="max-w-2xl">
        <h2 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight">
          <span className="text-accent">{heading}</span>
        </h2>
        <p className="mt-4 text-base text-background/70 leading-relaxed">{intro}</p>
      </div>

      {/* Routine selector */}
      <div className="mt-8 flex flex-wrap gap-2.5" data-testid="routine-selector">
        {routines.map((r) => {
          const isActive = r.id === routineId;
          return (
            <button
              key={r.id}
              data-testid={`routine-${r.id}`}
              onClick={() => setRoutineId(r.id)}
              className={`font-display font-bold text-sm px-4 py-2.5 rounded-full border-2 transition-all ${
                isActive
                  ? "bg-accent text-foreground border-accent -translate-y-0.5"
                  : "bg-transparent text-background/80 border-background/30 hover:border-background hover:-translate-y-0.5"
              }`}
              style={isActive ? { boxShadow: "4px 4px 0 0 rgba(253,251,247,0.9)" } : {}}
            >
              {t(`routineData.${r.id}.name`)}
            </button>
          );
        })}
      </div>

      {/* Progression chord track */}
      <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="chord-track">
        {routineChords.map((c, i) => {
          const isCurrent = i === stepIndex;
          return (
            <div
              key={`${c.id}-${i}`}
              className={`flex-1 min-w-[130px] rounded-xl brutal-border p-4 transition-all ${
                isCurrent ? "bg-accent text-foreground -translate-y-1" : "bg-background/5 text-background"
              }`}
              style={isCurrent ? { boxShadow: "5px 5px 0 0 rgba(253,251,247,0.9)" } : {}}
            >
              <p className="step-tag opacity-70">{i + 1}</p>
              <p className="font-display font-extrabold text-lg leading-tight">{chordLabel(c.name, lang)}</p>
              <p className="text-xs font-semibold opacity-70">
                {c.notes.map((m) => noteLabel(noteName(m).replace(/[0-9]/g, ""), lang)).join(" · ")}
              </p>
            </div>
          );
        })}
      </div>

      {/* Piano */}
      <div className="mt-8">
        <Piano activeMode={{ scale: running ? practiceFlash : [], root: null }} playingMidi={practiceFlash} />
      </div>

      {/* Controls */}
      <div className="mt-6 flex flex-wrap items-center gap-4">
        {!running ? (
          <button
            data-testid="practice-start-btn"
            onClick={startPractice}
            className="font-display font-bold bg-accent text-foreground px-6 py-3 rounded-full brutal-border hover:-translate-y-0.5 transition-transform flex items-center gap-2"
            style={{ boxShadow: "4px 4px 0 0 rgba(253,251,247,0.9)" }}
          >
            <Play className="w-4 h-4" /> {t("practice.start")}
          </button>
        ) : (
          <button
            data-testid="practice-stop-btn"
            onClick={stopPractice}
            className="font-display font-bold bg-primary text-primary-foreground px-6 py-3 rounded-full brutal-border hover:-translate-y-0.5 transition-transform flex items-center gap-2"
            style={{ boxShadow: "4px 4px 0 0 rgba(253,251,247,0.9)" }}
          >
            <Square className="w-4 h-4" /> {t("practice.stop")}
          </button>
        )}

        <div className="flex items-center gap-2" data-testid="tempo-control">
          <span className="step-tag text-background/70">{t("practice.speed")}</span>
          {[
            { key: "slow", ms: 2100 },
            { key: "medium", ms: 1500 },
            { key: "fast", ms: 950 },
          ].map((sp) => (
            <button
              key={sp.key}
              data-testid={`tempo-${sp.key}`}
              onClick={() => setTempo(sp.ms)}
              className={`text-sm font-bold px-3 py-1.5 rounded-full border-2 transition-colors ${
                tempo === sp.ms
                  ? "bg-background text-foreground border-background"
                  : "border-background/30 text-background/80 hover:border-background"
              }`}
            >
              {t(`practice.${sp.key}`)}
            </button>
          ))}
        </div>

        <span className="flex items-center gap-2 text-sm text-background/60">
          <Repeat className="w-4 h-4" /> {t("practice.loops")}
        </span>
      </div>

      <div className="mt-8 bg-secondary text-secondary-foreground brutal-border rounded-2xl p-6 max-w-2xl">
        <p className="font-display font-bold text-lg mb-1">{t(`routineData.${routine.id}.name`)}</p>
        <p className="step-tag opacity-80">{routine.numerals}</p>
        <p className="text-sm mt-2 leading-relaxed">{t(`routineData.${routine.id}.subtitle`)} {t("practice.tip")}</p>
      </div>
    </div>
  );
};
